// components/avatar-creator/Peep.js
import React, { useLayoutEffect, useState } from "react";

import avatarParts from "./avatarParts";
import ReactionVisual from "./ReactionVisual";
import { getSticker } from "../stickerData";

const SKIN_COLORS = [
  "#694D3D",
  "#AE5D29",
  "#D08B5B",
  "#EDB98A",
  "#FFDBB4",
  "#FFD11B",
];

const normalizeColor = (color, fallback) => {
  if (typeof color !== "string" || !color.trim()) {
    return fallback;
  }

  const value = color.trim();
  return value.startsWith("#") ? value : `#${value}`;
};

const randomValue = (category) => {
  const keys = Object.keys(avatarParts[category] || {});
  return keys[Math.floor(Math.random() * keys.length)];
};

const createRandomAvatar = () => ({
  head: randomValue("head"),
  accessories: randomValue("accessories"),
  body: randomValue("body"),
  facialHair: randomValue("facialHair"),
  face: randomValue("face"),
  skinColor: SKIN_COLORS[Math.floor(Math.random() * SKIN_COLORS.length)],
  hairColor: "#000000",
});

const normalizeAvatar = (useravatar = {}) => ({
  ...useravatar,
  accessories: avatarParts.accessories[useravatar.accessories]
    ? useravatar.accessories
    : "None",
  body: avatarParts.body[useravatar.body]
    ? useravatar.body
    : "StripedPocketTee",
  face: avatarParts.face[useravatar.face]
    ? useravatar.face
    : "Blank",
  facialHair: avatarParts.facialHair[useravatar.facialHair]
    ? useravatar.facialHair
    : "None",
  head: avatarParts.head[useravatar.head]
    ? useravatar.head
    : "Bun",
  skinColor: normalizeColor(useravatar.skinColor, "#FFDBB4"),
  hairColor: normalizeColor(useravatar.hairColor, "#000000"),
});

const Peep = ({
  useravatar,
  size,
  width,
  height,
  borderRadius,
  borderColor,
  borderWidth,
  overflow = "hidden",
  backgroundColor = "#ffffff",
  tiny,
  flip,
  reaction = null,
  reactionInteractive = true,
}) => {
  const [avatar, setAvatar] = useState({});
  const [background, setBackground] = useState(backgroundColor);

  useLayoutEffect(() => {
    const sticker = getSticker(reaction);
    const baseAvatar = useravatar
      ? normalizeAvatar(useravatar)
      : createRandomAvatar();

    setAvatar({
      ...baseAvatar,
      ...(sticker?.avatar || {}),
    });

    setBackground(
      reaction
        ? sticker?.background || backgroundColor
        : backgroundColor,
    );
  }, [useravatar, reaction, backgroundColor]);

  const HeadComponent = avatarParts.head[avatar.head];
  const AccessoriesComponent =
    avatarParts.accessories[avatar.accessories];
  const BodyComponent = avatarParts.body[avatar.body];
  const FacialHairComponent =
    avatarParts.facialHair[avatar.facialHair];
  const FaceComponent = avatarParts.face[avatar.face];

  const skinColor = normalizeColor(
    avatar.skinColor,
    "#FFDBB4",
  );

  const hairColor = normalizeColor(
    avatar.hairColor,
    "#000000",
  );

  const containerStyle = {
    position: "relative",
    width,
    height,
    borderRadius,
    borderColor,
    borderWidth,
    backgroundColor: background,
    overflow,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderStyle: borderWidth ? "solid" : undefined,
    transform: flip ? "scaleX(-1)" : "none",
  };

  return (
    <div style={containerStyle}>
      <svg
        className="peep-avatar-svg"
        width={size}
        height={size * 1.5}
        viewBox="-50 -120 1250 1650"
      >
        <g
          transform={
            tiny
              ? "translate(20, 250)"
              : "translate(0, 150)"
          }
        >
          {!tiny && (
            <g transform="translate(147, 639)">
              {BodyComponent && (
                <BodyComponent
                  fill={skinColor}
                  skinColor={skinColor}
                  hairColor={hairColor}
                />
              )}
            </g>
          )}

          <g transform="translate(372, 180)">
            {HeadComponent && (
              <HeadComponent
                fill={skinColor}
                skinColor={skinColor}
                hairColor={hairColor}
              />
            )}
          </g>

          <g transform="translate(495, 518)">
            {FacialHairComponent && (
              <FacialHairComponent
                fill={hairColor}
                skinColor={skinColor}
                hairColor={hairColor}
              />
            )}
          </g>

          <g transform="translate(531, 366)">
            {FaceComponent && (
              <FaceComponent
                fill="#000000"
                skinColor={skinColor}
                hairColor={hairColor}
              />
            )}
          </g>

          <g transform="translate(419, 421)">
            {AccessoriesComponent && (
              <AccessoriesComponent
                fill="#000000"
                skinColor={skinColor}
                hairColor={hairColor}
              />
            )}
          </g>
        </g>
      </svg>

      {reaction && (
        <ReactionVisual
          reaction={reaction}
          interactive={reactionInteractive}
        />
      )}
    </div>
  );
};

export default Peep;